import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Spinner, Alert, Button, Modal, Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQrcode, faBook, faExclamationTriangle, faFile, faFolderOpen, faShare } from '@fortawesome/free-solid-svg-icons';
import axios from '../utils/api';
import { API_BASE } from '../utils/env';
import { toast } from 'react-toastify';

const Gallery = () => {
  const [pdfs, setPdfs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchPdfs();
  }, []);

  // Share modal state
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareLink, setShareLink] = useState('');

  // QR Code modal state
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrCodeImage, setQrCodeImage] = useState('');
  const [qrCodePdfId, setQrCodePdfId] = useState(null);
  const [qrCodeLoading, setQrCodeLoading] = useState(false);

  const openShareModal = (slug) => {
    const url = `${window.location.origin}/viewer/document/${slug}`;
    setShareLink(url);
    setShowShareModal(true);
  };

  const closeShareModal = () => {
    setShowShareModal(false);
    setShareLink('');
  };

  const fetchPdfs = async () => {
    try {
      const response = await axios.get('/api/pdf');
      setPdfs(response.data);
    } catch (err) {
      setError('Erro ao carregar a galeria');
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleDownloadQRCode = async (pdfId) => {
    try {
      setQrCodeLoading(true);
      setQrCodePdfId(pdfId);
      setShowQRModal(true);
      
      // Primeiro, gera o QR code
      await axios.post(`/api/qrcode/${pdfId}/generate`);
      
      // Depois, obtém a imagem do QR code
      const imageUrl = `${API_BASE}/api/qrcode/${pdfId}/image`;
      setQrCodeImage(imageUrl);
      
    } catch (error) {
      console.error('Erro ao gerar QR code:', error);
      toast.error('Erro ao gerar QR code');
      setShowQRModal(false);
    } finally {
      setQrCodeLoading(false);
    }
  };

  const closeQRModal = () => {
    setShowQRModal(false);
    setQrCodeImage('');
    setQrCodePdfId(null);
    setQrCodeLoading(false);
  };

  const downloadQRCode = () => {
    if (qrCodePdfId) {
      window.open(`${API_BASE}/api/qrcode/${qrCodePdfId}/download`, '_blank');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  // Safe clipboard copy: uses Clipboard API when available; falls back to execCommand on HTTP/insecure contexts
  const copyToClipboard = async (text) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (e) {}
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'absolute';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      return true;
    } catch (e) {
      return false;
    }
  };

  if (loading) {
    return (
      <div className="min-vh-100" style={{ backgroundColor: '#f1f5f9' }}>
        <Container className="py-5">
          <div className="text-center" style={{ marginTop: '10rem' }}>
            <div 
              className="d-inline-flex align-items-center justify-content-center mb-4"
              style={{
                width: '80px',
                height: '80px',
                backgroundColor: '#00B2A9',
                borderRadius: '20px'
              }}
            >
              <FontAwesomeIcon icon={faBook} style={{ fontSize: '2rem', color: 'white' }} />
            </div>
            <div className="mb-4">
              <Spinner 
                animation="border" 
                role="status" 
                style={{ color: '#00B2A9', width: '3rem', height: '3rem' }}
              >
                <span className="visually-hidden">Carregando...</span>
              </Spinner>
            </div>
            <h3 className="mb-2" style={{ color: '#002F6C' }}>Carregando galeria...</h3>
            <p style={{ color: '#64748b' }}>Aguarde enquanto buscamos os documentos</p>
          </div>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-vh-100" style={{ backgroundColor: '#f1f5f9' }}>
        <Container className="py-5">
          <div 
            className="p-4 rounded-3 text-center"
            style={{ backgroundColor: '#ffffff', marginTop: '5rem', border: '1px solid #e2e8f0' }}
          >
            <div className="mb-3">
              <FontAwesomeIcon icon={faExclamationTriangle} style={{ fontSize: '3rem', color: '#dc3545' }} />
            </div>
            <h3 className="mb-2" style={{ color: '#002F6C' }}>Ops! Algo deu errado</h3>
            <p className="mb-4" style={{ color: '#64748b' }}>{error}</p>
            <Button 
              onClick={() => window.location.reload()}
              style={{ backgroundColor: '#00B2A9', borderColor: '#00B2A9' }}
            >
              Tentar novamente
            </Button>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="min-vh-100" style={{ backgroundColor: '#f1f5f9' }}>
      {/* Header Section */}
      <Container className="py-3">
        <Row className="mb-4">
          <Col>
            <div className="text-center">
              <div 
                className="d-inline-flex align-items-center justify-content-center mb-3"
                style={{
                  width: '60px',
                  height: '60px',
                  backgroundColor: '#00B2A9',
                  borderRadius: '18px'
                }}
              >
                <FontAwesomeIcon icon={faBook} style={{ fontSize: '1.6rem', color: 'white' }} />
              </div>
              
              <h1 className="h2 fw-bold mb-2" style={{ color: '#002F6C' }}>
                Galeria de PDFs
              </h1>
              
              <p className="mb-3" style={{ color: '#64748b' }}>
                Explore nossa coleção de documentos
              </p>
              
              {pdfs.length > 0 && (
                <div className="d-flex align-items-center justify-content-center">
                  <div 
                    className="d-flex align-items-center px-3 py-2 rounded-pill"
                    style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0' }}
                  >
                    <div 
                      className="me-2"
                      style={{
                        width: '6px',
                        height: '6px',
                        backgroundColor: '#00B2A9',
                        borderRadius: '50%'
                      }}
                    ></div>
                    <span className="fw-semibold" style={{ color: '#002F6C', fontSize: '0.9rem' }}>
                      <span style={{ color: '#00B2A9' }}>{pdfs.length}</span> documento{pdfs.length !== 1 ? 's' : ''} {pdfs.length !== 1 ? 'disponíveis' : 'disponível'}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </Col>
        </Row>
      </Container>

      <Container className="pb-4">
        {pdfs.length === 0 ? (
          <Row className="justify-content-center">
            <Col lg={8}>
              <div 
                className="text-center py-5 rounded-3"
                style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0' }}
              >
                <div className="mb-4">
                  <FontAwesomeIcon icon={faBook} style={{ fontSize: '5rem', color: '#00B2A9' }} />
                </div>
                <h3 className="mb-3" style={{ color: '#002F6C' }}>Nenhum PDF encontrado</h3>
                <p className="mb-0" style={{ color: '#64748b' }}>
                  Ainda não há documentos na galeria. Seja o primeiro a fazer upload!
                </p>
              </div>
            </Col>
          </Row>
        ) : (
          <Row>
            {pdfs.map((pdf) => (
              <Col key={pdf.id} md={6} lg={4} xl={3} className="mb-4">
                <Card 
                  className="h-100 border-0 shadow-lg"
                  style={{ 
                    backgroundColor: '#ffffff',
                    borderRadius: '15px',
                    transition: 'all 0.3s ease',
                    overflow: 'hidden',
                    border: '1px solid #e2e8f0'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-8px)';
                    e.currentTarget.style.boxShadow = '0 10px 40px rgba(0, 178, 169, 0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
                  }}
                >
                  <div className="position-relative">
                    {pdf.thumbnailPath ? (
                      <Card.Img
                        variant="top"
                        src={`${API_BASE}/api/pdf/${pdf.id}/thumbnail?v=${pdf.sizeBytes || Date.now()}`}
                        style={{ 
                          height: '250px', 
                         
                          filter: 'brightness(0.9)'
                        }}
                      />
                    ) : (
                      <div
                        className="d-flex align-items-center justify-content-center"
                        style={{ 
                          height: '250px',
                          backgroundColor: '#002F6C'
                        }}
                      >
                        <div className="text-center">
                          <div className="mb-2">
                            <FontAwesomeIcon icon={faFile} style={{ fontSize: '3rem', color: '#00B2A9' }} />
                          </div>
                          <small style={{ color: '#00B2A9' }}>PDF</small>
                        </div>
                      </div>
                    )}
                    
                    {/* Overlay gradient */}
                    <div 
                      className="position-absolute bottom-0 start-0 w-100"
                      style={{
                        height: '50px',
                        background: 'linear-gradient(transparent, rgba(0, 47, 108, 0.7))'
                      }}
                    ></div>
                    
                    <div className="position-absolute top-0 end-0 m-2">
                      <span 
                        className="badge px-2 py-1 small"
                        style={{ 
                          backgroundColor: '#00B2A9',
                          color: 'white',
                          borderRadius: '8px'
                        }}
                      >
                        {pdf.pageCount} páginas
                      </span>
                    </div>
                  </div>
                  
                  <Card.Body className="d-flex flex-column p-3">
                    <Card.Title 
                      className="h6 mb-2 fw-semibold" 
                      style={{ 
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        minHeight: '2.4rem',
                        color: '#002F6C'
                      }}
                    >
                      {pdf.name}
                    </Card.Title>
                    
                    {pdf.description && (
                      <Card.Text 
                        className="small mb-3 flex-grow-1"
                        style={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          fontSize: '0.85rem',
                          color: '#64748b'
                        }}
                      >
                        {pdf.description}
                      </Card.Text>
                    )}
                    
                    <div className="mt-auto">
                      {/* File info */}
                      <div className="d-flex justify-content-between align-items-center mb-3 pt-2" style={{ borderTop: '1px solid rgba(0, 178, 169, 0.2)' }}>
                        <small style={{ color: '#00B2A9', fontSize: '0.75rem' }}>
                          {formatFileSize(pdf.sizeBytes)}
                        </small>
                        <small style={{ color: '#64748b', fontSize: '0.75rem' }}>
                          {formatDate(pdf.uploadDate)}
                        </small>
                      </div>
                      
                      {/* Action buttons */}
                      <div className="d-flex gap-2">
                        <Button
                          as={Link}
                          to={`/viewer/${pdf.id}`}
                          size="sm"
                          className="flex-fill fw-semibold"
                          style={{ 
                            backgroundColor: '#00B2A9', 
                            borderColor: '#00B2A9',
                            borderRadius: '8px',
                            fontSize: '0.85rem'
                          }}
                        >
                          <FontAwesomeIcon icon={faFolderOpen} className="me-2" style={{ color: 'white' }} />
                          Abrir
                        </Button>
                        {pdf.allowShare && (
                          <Button
                              variant="outline-light"
                              size="sm"
                              onClick={() => openShareModal(pdf.slug)}
                              style={{ 
                                borderColor: 'rgba(100, 116, 139, 0.3)',
                                borderRadius: '8px',
                                width: '40px'
                              }}
                              title="Compartilhar"
                            >
                              <FontAwesomeIcon icon={faShare} style={{ color: '#64748b' }} />
                            </Button>
                        )}
                        <Button
                          variant="outline-light"
                          size="sm"
                          onClick={() => handleDownloadQRCode(pdf.id)}
                          style={{ 
                            borderColor: 'rgba(100, 116, 139, 0.3)',
                            borderRadius: '8px',
                            width: '40px',
                            color: '#000000'
                          }}
                          title="Baixar QR Code"
                        >
                <FontAwesomeIcon icon={faQrcode} className="me-2" style={{ color: '#000000' }} />
                        </Button>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        )}

        {/* Stats Footer - REMOVIDO */}
      </Container>

      {/* QR Code Modal */}
      <Modal 
        show={showQRModal} 
        onHide={closeQRModal}
        centered
        backdrop="static"
      >
        <div style={{ borderRadius: '12px', overflow: 'hidden' }}>
          <Modal.Header 
            closeButton 
            className="border-0 pb-2" 
            style={{ backgroundColor: '#ffffff' }}
          >
            <Modal.Title className="d-flex align-items-center" style={{ color: '#002F6C' }}>
              <FontAwesomeIcon icon={faQrcode} className="me-2" />
              QR Code
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ backgroundColor: '#ffffff', textAlign: 'center' }}>
            {qrCodeLoading ? (
              <div className="d-flex flex-column align-items-center py-4">
                <Spinner animation="border" style={{ color: '#00B2A9' }} />
                <p className="mt-3 mb-0" style={{ color: '#002F6C' }}>Gerando QR Code...</p>
              </div>
            ) : (
              qrCodeImage && (
                <div className="d-flex flex-column align-items-center">
                  <img 
                    src={qrCodeImage} 
                    alt="QR Code" 
                    style={{ 
                      maxWidth: '250px', 
                      maxHeight: '250px',
                      border: '2px solid #f1f5f9',
                      borderRadius: '8px',
                      padding: '10px',
                      backgroundColor: '#ffffff'
                    }} 
                  />
                  <p className="mt-3 mb-0 small" style={{ color: '#666' }}>
                    Escaneie o QR Code para acessar o documento
                  </p>
                </div>
              )
            )}
          </Modal.Body>
          <Modal.Footer className="border-0 pt-2" style={{ backgroundColor: '#ffffff' }}>
            <Button 
              variant="outline-secondary" 
              onClick={closeQRModal}
              style={{ borderRadius: '8px' }}
            >
              Fechar
            </Button>
            {!qrCodeLoading && qrCodeImage && (
              <Button
                onClick={downloadQRCode}
                style={{
                  backgroundColor: '#00B2A9',
                  borderColor: '#00B2A9',
                  borderRadius: '8px'
                }}
              >
                Baixar QR Code
              </Button>
            )}
          </Modal.Footer>
        </div>
      </Modal>

      {/* Share Modal */}
      <Modal 
        show={showShareModal} 
        onHide={closeShareModal} 
        centered
        contentClassName="border-0"
        style={{ backdropFilter: 'blur(10px)' }}
      >
        <div style={{ backgroundColor: '#ffffff', borderRadius: '15px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
          <Modal.Header 
            closeButton 
            className="border-0 pb-2"
            style={{ backgroundColor: '#ffffff' }}
          >
            <Modal.Title className="d-flex align-items-center" style={{ color: '#002F6C' }}>
              <FontAwesomeIcon icon={faShare} className="me-2" style={{ color: '#002F6C' }} />
              Compartilhar link
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ backgroundColor: '#ffffff' }}>
            <Form.Group>
              <Form.Label className="mb-2 small fw-semibold" style={{ color: '#002F6C' }}>Link público</Form.Label>
              <div className="d-flex gap-2">
                <Form.Control 
                  type="text" 
                  value={shareLink} 
                  readOnly 
                  onFocus={(e) => e.target.select()}
                  style={{
                    backgroundColor: '#f1f5f9',
                    borderColor: 'rgba(0, 178, 169, 0.3)',
                    color: '#002F6C',
                    borderRadius: '8px'
                  }}
                />
                <Button
                  onClick={async () => {
                    const ok = await copyToClipboard(shareLink);
                    if (ok) {
                      toast.success('Link copiado!');
                    } else {
                      const input = document.querySelector('input[type="text"][value="' + shareLink.replace(/"/g, '\\"') + '"]');
                      if (input) input.select();
                      toast.error('Não foi possível copiar automaticamente. Selecione e copie o link.');
                    }
                  }}
                  style={{
                    backgroundColor: '#00B2A9',
                    borderColor: '#00B2A9',
                    borderRadius: '8px'
                  }}
                >
                  📋 Copiar
                </Button>
              </div>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="border-0 pt-2" style={{ backgroundColor: '#ffffff' }}>
            <Button 
              variant="outline-secondary" 
              onClick={closeShareModal}
              style={{ borderRadius: '8px' }}
            >
              Fechar
            </Button>
          </Modal.Footer>
        </div>
      </Modal>
    </div>
  );
};

export default Gallery;